import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import dayjs from "dayjs";
import LandscapeProgramLayout from "../LandscapeProgramLayout";
import { useAuthContext } from "../../../context/auth";
import BottomNav from "../BottomNav";
import api from "../../../lib/api";
import { HiroxSessionsDetailData } from "../../../Api";

const DIFFICULTY_GRADIENTS: Record<string, string> = {
  EASY: "linear-gradient(135deg, #E3FAFF 0%, #53CAF5 100%)",
  NORMAL: "linear-gradient(135deg, #FFF5E0 0%, #F6B731 100%)",
  HARD: "linear-gradient(135deg, #FFEBEB 0%, #FF5757 100%)",
};

const DIFFICULTY_LABELS: Record<string, string> = {
  EASY: "Easy",
  NORMAL: "Normal",
  HARD: "Hard",
};

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function HiluxWorkout() {
  const { did, hiluxSessionId } = useAuthContext();
  const [teamsData, setTeamsData] = useState<HiroxSessionsDetailData | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef(Date.now());

  const [startTime] = useState(() => dayjs().format("YYYY-MM-DD HH:mm:ss"));

  const qrUrl = useMemo(() => {
    return `https://dev.crm.abrtraining.co.kr/hyrox?sessionId=${hiluxSessionId}&startTime=${encodeURIComponent(startTime)}&_vercel_share=Ri36lrKc8RjMNFf5KmivyrMDa3BjuXdl`;
  }, [hiluxSessionId, startTime]);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchTeams = useCallback(() => {
    if (!hiluxSessionId) return;
    api.fits.hiroxSessionsDetail(hiluxSessionId).then((res) => {
      setTeamsData(res.data);
    });
  }, [hiluxSessionId]);

  useEffect(() => {
    fetchTeams();
    const interval = setInterval(fetchTeams, 3000);
    return () => clearInterval(interval);
  }, [fetchTeams]);

  const handleRecord = (hiroxTeamId: number) => {
    api.fits
      .hiroxRecordsCreate({
        hiroxTeamId,
        time: elapsed,
      })
      .then(() => {
        fetchTeams();
      });
  };

  const getRank = (team: {
    teamNumber: number;
    difficulty: string;
    time: number | null;
  }): number | null => {
    if (!teamsData?.teams || team.time === null) return null;
    const sameDifficulty = teamsData.teams
      .filter((t) => t.difficulty === team.difficulty && t.time !== null)
      .sort((a, b) => a.time! - b.time!);
    const idx = sameDifficulty.findIndex(
      (t) => t.teamNumber === team.teamNumber,
    );
    return idx >= 0 ? idx + 1 : null;
  };

  if (did !== "0") {
    return (
      <LandscapeProgramLayout step={5}>
        <div className="h-full w-full flex flex-col items-center justify-center gap-[100px]">
          <h1
            className="text-[100px] font-extrabold uppercase text-center gradient-text"
            style={{ textShadow: "0px 0px 30px rgba(0,0,0,0.25)" }}
          >
            Race Time
          </h1>
          <div className="flex items-center justify-between w-[1400px]">
            <div className="w-[500px] h-[500px] bg-white rounded-[40px] flex items-center justify-center overflow-hidden p-[40px]">
              <QRCodeSVG value={qrUrl} size={420} />
            </div>
            <div className="flex flex-col gap-[80px] items-center w-[693px]">
              <p
                className="text-[140px] font-extrabold text-white text-center uppercase w-full"
                style={{
                  textShadow: "0px 0px 30px rgba(0,0,0,0.25)",
                  lineHeight: "140px",
                }}
              >
                {String(Math.floor(elapsed / 60)).padStart(2, "0")}:
                {String(elapsed % 60).padStart(2, "0")}
              </p>
              <p
                className="text-[48px] font-bold text-[#7DE9FF] text-center uppercase"
                style={{ lineHeight: "70px" }}
              >
                QR 스캔하고 우리 팀의 기록을{"\n"}앱에서 확인하세요!
              </p>
            </div>
          </div>
        </div>
        <BottomNav />
      </LandscapeProgramLayout>
    );
  }

  return (
    <LandscapeProgramLayout step={5}>
      <div className="h-full w-full flex relative">
        <div className="p-[60px] flex flex-col gap-[40px] items-center w-full">
          <div className="flex items-center justify-center w-full">
            <h1 className="gradient-text text-[70px] font-bold">RACE TIME</h1>
          </div>
          <div className="grid grid-cols-4 gap-x-[30px] gap-y-[24px] w-full">
            {Array.from({ length: 8 }, (_, stIdx) => {
              const team = teamsData?.teams.find(
                (t) => t.teamNumber === stIdx + 1,
              );
              const rank = team ? getRank(team) : null;
              return (
                <div
                  key={stIdx}
                  className="flex flex-col gap-[16px] items-center"
                >
                  <div className="flex items-center justify-between w-full">
                    <h2
                      className="text-[36px] font-extrabold text-white uppercase"
                      style={{
                        textShadow: "0px 0px 30px rgba(0,0,0,0.25)",
                      }}
                    >
                      TEAM {stIdx + 1}
                    </h2>
                    {team && (
                      <span
                        className="text-[24px] font-extrabold capitalize"
                        style={{
                          backgroundImage:
                            DIFFICULTY_GRADIENTS[team.difficulty],
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          color: "transparent",
                        }}
                      >
                        {DIFFICULTY_LABELS[team.difficulty]}
                      </span>
                    )}
                  </div>
                  <div className="relative p-[20px] pb-[72px] gap-x-[30px] gap-y-[20px] flex flex-wrap bg-white/[0.12] min-h-[260px] max-h-[260px] w-full rounded-[12px] overflow-y-auto scrollbar-hide items-start content-start">
                    {team?.members.map((member) => (
                      <div
                        className="flex items-center gap-[12px]"
                        key={member.id}
                      >
                        <img
                          src={
                            member.profileImageUrl || "/defaultProfileImage.png"
                          }
                          alt="profile"
                          className="w-[36px] h-[36px] rounded-full"
                        />
                        <span className="text-[18px] text-white text-center">
                          {member.name}
                        </span>
                      </div>
                    ))}
                    {team && (
                      <div className="absolute bottom-[20px] left-[20px] right-[20px] flex items-center justify-between">
                        {team.time !== null ? (
                          <>
                            <div className="flex items-center gap-[20px]">
                              <div className="bg-[#E7EDF6] px-[20px] py-[12px] rounded-[8px] w-[120px] text-center">
                                <span className="text-[20px] font-medium text-[#020A1F] capitalize">
                                  완주 완료
                                </span>
                              </div>
                              <span className="text-[30px] font-medium text-white capitalize">
                                {formatTime(team.time)}
                              </span>
                            </div>
                            {rank && (
                              <span
                                className="text-[30px] font-medium capitalize"
                                style={{
                                  backgroundImage:
                                    DIFFICULTY_GRADIENTS[team.difficulty],
                                  backgroundClip: "text",
                                  WebkitBackgroundClip: "text",
                                  color: "transparent",
                                }}
                              >
                                {rank}위
                              </span>
                            )}
                          </>
                        ) : (
                          <button
                            className="bg-[#7DE9FF] px-[20px] py-[12px] rounded-[8px] w-[120px] mx-auto"
                            onClick={() => handleRecord(team.id)}
                          >
                            <span className="text-[20px] font-medium text-[#020A1F] capitalize">
                              완주 기록
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <BottomNav />
    </LandscapeProgramLayout>
  );
}

export default HiluxWorkout;
